/**
 * wrapt-header.js -- the header's behaviour from the Header menu v2 handoff
 * (12 Oct 2026). The theme's Header component (global.bundle.js, built from a
 * source that is not in this repo) still owns the dropdowns' state,
 * menuOpen.menuN; this script only sets it.
 *
 *   - Hover opens a dropdown after 120ms, at once when another is already open,
 *     and leaving the header closes it after 250ms. A click still toggles, but
 *     a click straight after hover opened it keeps it open. Pointer-and-hover
 *     screens only, so a tap never opens and closes in one go.
 *   - ArrowDown on a trigger opens it and focuses its first link. Escape closes
 *     it and gives focus back to the trigger. The theme's own Escape handler
 *     read a global, dropdownCount, that nothing defined, and threw; it is
 *     defined here.
 *   - A scrim over the page while a dropdown is open.
 *   - Shop Gifts: hovering or focusing a link swaps the preview to it
 *     (snippets/wrapt-mega-shop.liquid).
 *   - The bag's count badge pops when the count goes up.
 *   - window.WraptHeader.added({ name, sub, price, image, url }): the add-to-cart
 *     popover under the bag (ready-made pages, assets/wrapt-ready-made-v2.js).
 */
(() => {
  /* The theme's keyup handler loops over a global `dropdownCount` that nothing
     defines, so every Escape threw a ReferenceError. Defined here (set to the
     real count once bound), its own Escape works too. */
  if (typeof window.dropdownCount === 'undefined') window.dropdownCount = 0;

  const HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
  const DESK = window.matchMedia('(min-width: 1024px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 250;
  const HIDE_AFTER = 6000;

  let root = null;
  let state = null;
  let scrim = null;
  let tOpen = 0;
  let tClose = 0;
  let openedByHoverAt = 0;

  const keys = () => (state && state.menuOpen ? Object.keys(state.menuOpen) : []);
  const openKey = () => keys().find((k) => state.menuOpen[k]) || null;
  const setOpen = (key) => {
    keys().forEach((k) => {
      state.menuOpen[k] = k === key;
    });
  };
  const triggerFor = (key) => document.querySelector(`button[data-dropdown-parent="${key}"]`);

  function dataOf(el) {
    if (window.Alpine && typeof window.Alpine.$data === 'function') {
      try {
        return window.Alpine.$data(el);
      } catch (_) {
        /* fall through */
      }
    }
    return el && el._x_dataStack ? el._x_dataStack[0] : null;
  }

  function syncScrim() {
    if (scrim) scrim.classList.toggle('is-on', !!openKey() && DESK.matches);
  }

  function bind() {
    if (state) return true;
    root = document.querySelector('[x-data^="Header("]');
    const d = root ? dataOf(root) : null;
    if (!d || !d.menuOpen) return false;
    state = d;
    window.dropdownCount = Object.keys(state.menuOpen).length;

    scrim = document.createElement('div');
    scrim.className = 'wrapt-scrim';
    scrim.setAttribute('aria-hidden', 'true');
    scrim.addEventListener('click', () => setOpen(null));
    document.body.appendChild(scrim);

    root.querySelectorAll('button[data-dropdown-parent]').forEach((btn) => {
      const key = btn.getAttribute('data-dropdown-parent');
      btn.addEventListener('mouseenter', () => {
        if (!HOVER.matches || !DESK.matches) return;
        clearTimeout(tClose);
        clearTimeout(tOpen);
        if (state.menuOpen[key]) return;
        const go = () => {
          setOpen(key);
          openedByHoverAt = Date.now();
        };
        if (openKey()) go();
        else tOpen = setTimeout(go, OPEN_DELAY);
      });
      btn.addEventListener('mouseleave', () => clearTimeout(tOpen));
      btn.addEventListener(
        'click',
        (e) => {
          clearTimeout(tOpen);
          if (HOVER.matches && state.menuOpen[key] && Date.now() - openedByHoverAt < 700) {
            e.stopImmediatePropagation();
            e.preventDefault();
          }
        },
        true
      );
      btn.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowDown') return;
        e.preventDefault();
        setOpen(key);
        setTimeout(() => {
          const panel = document.getElementById(key);
          const first = panel && panel.querySelector('a[href], button:not([disabled])');
          if (first) first.focus();
        }, 60);
      });
      new MutationObserver(syncScrim).observe(btn, { attributes: true, attributeFilter: ['aria-expanded'] });
    });

    root.querySelectorAll('ul[x-ref="navigation"] > li > a').forEach((a) => {
      a.addEventListener('mouseenter', () => {
        if (!HOVER.matches) return;
        clearTimeout(tOpen);
        if (openKey()) setOpen(null);
      });
    });
    root.addEventListener('mouseleave', () => {
      if (!HOVER.matches || !DESK.matches) return;
      clearTimeout(tOpen);
      tClose = setTimeout(() => setOpen(null), CLOSE_DELAY);
    });
    root.addEventListener('mouseenter', () => clearTimeout(tClose));
    DESK.addEventListener('change', syncScrim);
    return true;
  }

  /* ---- Shop Gifts preview -------------------------------------------------- */

  function preview(link) {
    const box = link.closest('[data-wmm]');
    if (!box) return;
    box.querySelectorAll('.wmm__link.is-on').forEach((x) => {
      if (x !== link) x.classList.remove('is-on');
    });
    link.classList.add('is-on');
    const pv = box.querySelector('[data-wmm-pv]');
    if (!pv) return;
    const d = link.dataset;
    pv.setAttribute('href', link.getAttribute('href'));
    pv.classList.toggle('is-stock', d.wmmKind === 'stock');
    const title = d.wmmTitle || link.textContent.trim();
    pv.querySelector('[data-wmm-pv-title]').textContent = title;
    pv.querySelector('[data-wmm-pv-meta]').textContent = d.wmmMeta || '';
    pv.querySelector('[data-wmm-pv-cta]').textContent = d.wmmCta || '';
    const src = d.wmmImg || '';
    const ph = pv.querySelector('[data-wmm-pv-ph]');
    if (ph) {
      ph.hidden = !!src;
      ph.textContent = title;
    }
    const img = pv.querySelector('[data-wmm-pv-img]');
    if (!img) return;
    img.hidden = !src;
    if (!src || img.getAttribute('data-wmm-current') === src) return;
    img.setAttribute('data-wmm-current', src);
    ['srcset', 'data-hold-src', 'data-hold-srcset'].forEach((a) => img.removeAttribute(a));
    clearTimeout(img.wmmTimer);
    if (REDUCED.matches) {
      img.src = src;
      return;
    }
    img.style.opacity = '0';
    img.wmmTimer = setTimeout(() => {
      img.src = src;
      img.style.opacity = '';
    }, 120);
  }
  const previewFrom = (e) => {
    const a = e.target && e.target.closest ? e.target.closest('.wmm__link') : null;
    if (a) preview(a);
  };
  document.addEventListener('mouseover', previewFrom);
  document.addEventListener('focusin', previewFrom);

  /* ---- The bag badge ------------------------------------------------------- */

  function badges() {
    document.querySelectorAll('span[x-text="$store.cart_count.count"]').forEach((b) => {
      let last = parseInt(b.textContent, 10) || 0;
      new MutationObserver(() => {
        const n = parseInt(b.textContent, 10) || 0;
        if (n > last && !REDUCED.matches) {
          b.classList.remove('is-pop');
          void b.offsetWidth;
          b.classList.add('is-pop');
          setTimeout(() => b.classList.remove('is-pop'), 300);
        }
        last = n;
      }).observe(b, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ---- The add-to-cart popover --------------------------------------------- */

  let pop = null;
  let tHide = 0;
  let opener = null;
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const ARROW = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

  function bagLink() {
    return [...document.querySelectorAll('.shopify-section-group-header-group a[href$="/cart"]')].find(
      (a) => a.getClientRects().length
    );
  }

  function place() {
    if (!pop) return;
    const a = bagLink();
    const vw = document.documentElement.clientWidth;
    if (!a) {
      pop.style.top = '96px';
      pop.style.right = '16px';
      return;
    }
    const r = a.getBoundingClientRect();
    const centre = r.left + r.width / 2;
    pop.style.top = `${Math.round(r.bottom + 12)}px`;
    if (vw <= 760) {
      pop.style.left = '16px';
      pop.style.right = '16px';
      pop.style.setProperty('--notch', `${Math.max(16, Math.round(vw - 16 - centre - 8))}px`);
    } else {
      const right = Math.max(16, Math.round(vw - centre - 36));
      pop.style.left = 'auto';
      pop.style.right = `${right}px`;
      pop.style.setProperty('--notch', `${Math.max(16, Math.round(vw - right - centre - 8))}px`);
    }
  }

  function hide(restoreFocus) {
    if (!pop || !pop.classList.contains('is-on')) return;
    clearTimeout(tHide);
    const hadFocus = pop.contains(document.activeElement);
    pop.classList.remove('is-on');
    if (restoreFocus && hadFocus && opener && opener.isConnected) opener.focus();
  }

  function added(item) {
    const it = item || {};
    if (state) setOpen(null);
    if (!pop) {
      pop = document.createElement('div');
      pop.className = 'wrapt-atc';
      pop.setAttribute('role', 'dialog');
      pop.setAttribute('aria-label', 'Added to cart');
      pop.tabIndex = -1;
      document.body.appendChild(pop);
      pop.addEventListener('mouseenter', () => clearTimeout(tHide));
      pop.addEventListener('mouseleave', () => {
        clearTimeout(tHide);
        tHide = setTimeout(() => hide(false), HIDE_AFTER);
      });
      pop.addEventListener('click', (e) => {
        if (e.target.closest('[data-atc-close]')) hide(true);
      });
      window.addEventListener('resize', place, { passive: true });
    }
    const thumb = it.image ? `<img src="${esc(it.image)}" alt="" width="72" height="72">` : '<span class="wrapt-atc__ph"></span>';
    pop.innerHTML = `
      <div class="wrapt-atc__h">
        <span class="wrapt-atc__ok" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2a1625" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>
        <span>Added to your cart</span>
        <button type="button" class="wrapt-atc__x" data-atc-close aria-label="Close"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
      </div>
      <div class="wrapt-atc__i">${thumb}<div><strong>${esc(it.name)}</strong>${it.sub ? `<span>${esc(it.sub)}</span>` : ''}</div><span class="wrapt-atc__price">${esc(it.price)}</span></div>
      <div class="wrapt-atc__b"><a class="btn btn-plum" href="${esc(it.url || '/cart')}">View Cart ${ARROW}</a><button type="button" class="btn btn-ghost" data-atc-close>Continue Shopping</button></div>`;
    opener = document.activeElement;
    place();
    void pop.offsetWidth;
    pop.classList.add('is-on');
    pop.focus({ preventScroll: true });
    clearTimeout(tHide);
    tHide = setTimeout(() => hide(false), HIDE_AFTER);
  }

  /* ---- Escape ---------------------------------------------------------------- */

  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Escape') return;
      if (pop && pop.classList.contains('is-on')) {
        hide(true);
        e.stopImmediatePropagation();
        return;
      }
      const key = state ? openKey() : null;
      if (!key) return;
      setOpen(null);
      const btn = triggerFor(key);
      if (btn) btn.focus();
      e.stopImmediatePropagation();
    },
    true
  );

  window.WraptHeader = Object.assign(window.WraptHeader || {}, { added, close: () => hide(true) });

  function start() {
    badges();
    if (bind()) return;
    let tries = 0;
    const t = setInterval(() => {
      tries += 1;
      if (bind() || tries > 60) clearInterval(t);
    }, 100);
  }
  document.addEventListener('alpine:initialized', bind);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
